import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Star, Plus, Check, Heart, Crown, ExternalLink, ArrowLeft, Play, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import ServiceBadges from "../components/ServiceBadges";
import WhereToWatch from "../components/WhereToWatch";
import TitlePodcasts from "../components/podcast/TitlePodcasts";
import ShareKit from "../components/ShareKit";
import { GenreLinks } from "../components/GenreChips";
import Disclaimer from "../components/Disclaimer";
import api from "../lib/api";
import { useWatchlist } from "../hooks/useWatchlist";
import { useFavorites } from "../hooks/useFavorites";
import { useTheme } from "../context/ThemeContext";
import { useTop8 } from "../context/Top8Context";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../lib/track";

export default function TitleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { inList, toggle } = useWatchlist();
  const { inList: inFav, toggle: toggleFav } = useFavorites();
  const { layout } = useTheme();
  const { addToTop8, inTop8 } = useTop8();
  const { user } = useAuth();
  const overlayNav = layout.hero === "billboard";
  const flatHead = layout.hero === "masthead";
  const isPortal = layout.hero === "masthead"; // Early 2010s — wide catalog-portal detail layout
  const backdropH = layout.hero === "billboard" ? "h-[62vh] min-h-[420px]" : layout.hero === "spotlight" ? "h-[48vh] min-h-[340px]" : layout.hero === "wall" ? "h-[60vh] min-h-[420px]" : isPortal ? "h-[46vh] min-h-[340px]" : "h-[36vh] min-h-[240px]";
  const overlap = layout.hero === "billboard" ? "-mt-56" : layout.hero === "spotlight" ? "-mt-40" : layout.hero === "wall" ? "-mt-52" : isPortal ? "-mt-40" : "-mt-20";
  const containerMax = isPortal ? "max-w-[1400px]" : "max-w-6xl";
  const posterW = isPortal ? "w-52 sm:w-72" : "w-40 sm:w-56";
  const [t, setT] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setT(null);
    setNotFound(false);
    api.get(`/catalog/title/${id}`)
      .then(({ data }) => {
        setT(data);
        trackEvent("title_view", { title_id: data.id, title: data.title });
        api.post("/user/recent", { id: data.id }).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Navbar />
        <div className="flex h-[60vh] items-center justify-center text-[var(--text-secondary)]" data-testid="detail-not-found">Title not found.</div>
      </div>
    );
  }

  if (!t) {
    return (
      <div className="min-h-screen bg-[var(--bg-base)]">
        <Navbar />
        <div className="flex h-[60vh] items-center justify-center font-head text-2xl tracking-widest text-[var(--accent)] animate-pulse">Loading…</div>
      </div>
    );
  }

  const added = inList(t.id);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="detail-page">
      {overlayNav ? <div className="absolute top-0 left-0 right-0 z-50"><Navbar /></div> : <Navbar />}

      <section className={`relative w-full overflow-hidden ${backdropH}`} data-testid="detail-backdrop">
        <img src={t.img} alt={t.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] via-[var(--bg-base)]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--bg-base)]/80 to-transparent" />
      </section>

      <div className={`relative z-20 mx-auto ${containerMax} px-4 lg:px-14 pb-24 ${overlap}`}>
        <button onClick={() => navigate(-1)} className="mb-6 flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="detail-back-button">
          <ArrowLeft size={18} /> Back
        </button>

        <div className="flex flex-col gap-8 md:flex-row">
          <img
            src={t.poster || t.img}
            alt={t.title}
            onError={(e) => { e.currentTarget.src = t.img; }}
            className={`${posterW} flex-none self-start rounded-[var(--card-radius)] border border-[var(--border-subtle)] object-cover shadow-2xl aspect-[2/3]`}
            data-testid="detail-poster"
          />

          <div className="flex-1 fade-up">
            <h1 className={`font-head text-5xl sm:text-6xl text-[var(--text-primary)] ${flatHead ? "font-bold" : "tracking-wide"}`}>{t.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-[var(--text-secondary)]">
              {t.rating > 0 && <span className="flex items-center gap-1 font-semibold text-amber-500"><Star size={15} className="fill-amber-500" /> {t.rating.toFixed(1)}</span>}
              <span>{t.year}</span>
              {t.runtime ? <span>{t.runtime} min</span> : null}
              {t.indie ? (
                <span className="rounded bg-[var(--accent)] px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-[var(--accent-contrast)]" data-testid="detail-indie-badge">Indie</span>
              ) : (
                <span className="rounded border border-[var(--border-subtle)] px-2 py-0.5 text-xs uppercase tracking-wider text-[var(--text-secondary)]">{t.media_type === "tv" ? "Series" : "Film"}</span>
              )}
              <span className="uppercase tracking-wider text-[var(--text-muted)]"><GenreLinks genres={t.genres} sep=" • " /></span>
            </div>
            {t.director && <p className="mt-2 text-sm text-[var(--text-secondary)]" data-testid="detail-director">Directed by <span className="font-semibold text-[var(--text-primary)]">{t.director}</span></p>}

            <p className={`mt-5 ${isPortal ? "max-w-none" : "max-w-2xl"} text-base leading-relaxed text-[var(--text-secondary)]`}>{t.overview}</p>

            {!t.indie && <ServiceBadges id={t.id} className="mt-5 w-fit" />}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                onClick={() => toggle(t)}
                className={`inline-flex items-center gap-2 rounded-[var(--card-radius)] px-6 py-3 font-bold uppercase tracking-wide transition-colors ${
                  added ? "bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]" : "border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-primary)] hover:border-[var(--accent)]"
                }`}
                data-testid="detail-watchlist-toggle"
              >
                {added ? <Check size={20} /> : <Plus size={20} />} {added ? "In My List" : "Add to My List"}
              </button>
              <button
                onClick={() => toggleFav(t)}
                className={`inline-flex items-center gap-2 rounded-[var(--card-radius)] px-5 py-3 font-bold uppercase tracking-wide transition-colors ${
                  inFav(t.id) ? "bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]" : "border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-primary)] hover:border-[var(--accent)]"
                }`}
                data-testid="detail-favorite-toggle"
              >
                <Heart size={18} className={inFav(t.id) ? "fill-current" : ""} /> {inFav(t.id) ? "Favorited" : "Favorite"}
              </button>
              <button
                onClick={() => addToTop8(t)}
                className={`inline-flex items-center gap-2 rounded-[var(--card-radius)] px-5 py-3 font-bold uppercase tracking-wide transition-colors ${
                  inTop8(t.id) ? "bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]" : "border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-primary)] hover:border-[var(--accent)]"
                }`}
                data-testid="detail-top8-toggle"
              >
                <Crown size={18} className={inTop8(t.id) ? "fill-current" : ""} /> {inTop8(t.id) ? "In Top 8" : "Add to Top 8"}
              </button>
              {t.trailer_url && (
                <a href={t.trailer_url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-6 py-3 font-bold uppercase tracking-wide text-[var(--text-primary)] transition-colors hover:border-[var(--accent)]"
                  data-testid="detail-trailer-link">
                  <Play size={18} /> Trailer
                </a>
              )}
            </div>

            {/* Where to Watch */}
            {t.indie ? (
              <div className="mt-10" data-testid="indie-watch-section">
                <h2 className={`font-head text-2xl text-[var(--text-primary)] ${flatHead ? "font-bold" : "tracking-wide"}`}>Where to Watch</h2>
                <div className="mt-3">
                  {t.is_sample && <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--accent-hover)]" data-testid="indie-sample-note">Sample entry — replace with your own film</p>}
                  <a href={t.indie_watch?.url} target="_blank" rel="noopener noreferrer"
                    onClick={() => { trackEvent("provider_click", { provider: t.indie_watch?.label || "Indie", title_id: t.id }); api.post("/user/watch-progress", { id: t.id }).catch(() => {}); toast(`Opening ${t.indie_watch?.label || "the film"} — your spot here is saved.`); }}
                    className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-6 py-3 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)]"
                    data-testid="indie-watch-link">
                    {t.indie_watch?.label || "Watch now"} <ExternalLink size={16} />
                  </a>
                  <p className="mt-2 text-xs text-[var(--text-muted)]">Watch directly on the filmmaker's chosen platform.</p>
                  {!t.is_sample && (
                    <Link to={`/community/film/${t.id.replace("indie-", "")}`}
                      className="mt-4 inline-flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-accent)] px-5 py-2.5 text-sm font-bold uppercase tracking-wide text-[var(--accent-hover)] transition-colors hover:bg-[var(--accent)]/10"
                      data-testid="discuss-film-link">
                      <MessageSquare size={15} /> Discuss this film
                    </Link>
                  )}
                  {!t.is_sample && <div className="mt-3"><ShareKit kind="indie" slug={t.id.replace("indie-", "")} title={t.title} /></div>}
                </div>
              </div>
            ) : (
              <WhereToWatch t={t} user={user} flatHead={flatHead} />
            )}

            <TitlePodcasts titleId={t.id} flatHead={flatHead} />

            {/* Cast */}
            {t.cast?.length > 0 && (
              <div className="mt-10" data-testid="detail-cast">
                <h2 className={`font-head text-2xl text-[var(--text-primary)] ${flatHead ? "font-bold" : "tracking-wide"}`}>Cast</h2>
                <div className={`mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 ${isPortal ? "lg:grid-cols-6" : ""}`}>
                  {t.cast.map((c, i) => {
                    const inner = (
                      <>
                        <CastAvatar name={c.name} photo={c.photo} />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[var(--text-primary)]">{c.name}</p>
                          <p className="truncate text-xs text-[var(--text-secondary)]">{c.character}</p>
                        </div>
                      </>
                    );
                    return c.person_id ? (
                      <Link key={i} to={`/person/${c.person_id}`}
                        className="flex items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3 transition-colors hover:border-[var(--accent)]"
                        data-testid={`cast-link-${c.person_id}`}>
                        {inner}
                      </Link>
                    ) : (
                      <div key={i} className="flex items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Disclaimer />
    </div>
  );
}


function CastAvatar({ name, photo }) {
  const [broken, setBroken] = useState(false);
  if (photo && !broken) {
    return (
      <img
        src={photo}
        alt={name}
        loading="lazy"
        onError={() => setBroken(true)}
        className="h-12 w-12 flex-none rounded-full border border-[var(--border-subtle)] object-cover"
        data-testid="cast-photo"
      />
    );
  }
  return (
    <div className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-[var(--accent)]/20 font-head text-lg text-[var(--accent)]" data-testid="cast-initial">
      {(name || "?").charAt(0)}
    </div>
  );
}
